import { useFuzzySearchList } from '@nozbe/microfuzz/react';
import { isNotNil, sort } from 'rambda';
import { type MouseEventHandler, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { useTypedParams } from 'react-router-typesafe-routes';
import { Virtuoso } from 'react-virtuoso';
import { AvatarWithStatus } from '@/components/common/avatar-with-status';
import Linkable from '@/components/common/linkable';
import SearchInput from '@/components/common/search-input';
import PageLayout from '@/components/layouts/page-layout/page-layout';
import useBanGroupMember from '@/hooks/groups/use-ban-group-member';
import useGroupInfo from '@/hooks/groups/use-group-info';
import useGroupMembers from '@/hooks/groups/use-group-members';
import useKickGroupMember from '@/hooks/groups/use-kick-group-member';
import { useIsDesktop } from '@/hooks/use-user-platform';
import ROUTES from '@/routes';
import { useAuthStore } from '@/stores/auth';
import { makePrompt } from '@/stores/prompter';
import cn from '@/utils/merge-class-names';
import { GroupSettingHeader } from './settings';

function AddMemberLink({ className }: { className?: string }) {
  const { t } = useTranslation();
  return (
    <Link
      to={ROUTES.MESSAGES.GROUPS.SETTINGS.MEMBERS.$.INVITE.$path({ relative: true })}
      className={className}>
      <button
        type='button'
        className='btn btn-ghost btn-sm text-primary mode-embedded:text-primary-content'>
        {t('common.add')}
      </button>
    </Link>
  );
}

export default function GroupMembersIndexRoute() {
  const { id: groupId } = useTypedParams(ROUTES.MESSAGES.GROUPS.SETTINGS.MEMBERS);

  const { t } = useTranslation();
  const [keyword, setKeyword] = useState('');

  const userId = useAuthStore((s) => s.auth?.UserID);
  const { mutateAsync: banMember } = useBanGroupMember(groupId);
  const { mutateAsync: kickOutMember } = useKickGroupMember(groupId);
  const { members } = useGroupMembers(groupId);
  const { canManage, data: GroupInfo } = useGroupInfo(groupId);

  const isDesktop = useIsDesktop();

  const filteredMembers = useFuzzySearchList({
    list: Object.values(members),
    queryText: keyword,
    getText: (item) => [item.RemarkName, item.GroupMemberName, item.UserNickName].filter(isNotNil),
    mapResultItem: ({ item }) => item,
  });

  const handleBanMember =
    (memberId: number, unban = false): MouseEventHandler =>
    async (e) => {
      e.preventDefault();
      if (unban) {
        return banMember({ userId: memberId, ban: 'unban' });
      }

      return makePrompt({
        key: 'ban-member',
        title: t('conversation.setting.group.ban'),
        content: t('conversation.setting.group.ban-confirm'),
        promise: () => banMember({ userId: memberId, ban: 'ban' }),
      });
    };

  const handleKickOutMember =
    (memberId: number): MouseEventHandler =>
    async (e) => {
      e.preventDefault();

      return makePrompt({
        key: 'kick-out-member',
        title: t('conversation.setting.group.remove'),
        content: t('conversation.kick-out-confirm-message'),
        resolveButtonLabel: t('conversation.setting.group.remove'),
        promise: () => kickOutMember({ userId: memberId, purgeMessages: false }),
      });
    };

  return (
    <PageLayout
      title={t('conversation.setting.group.members')}
      className='desktop:-mt-4'
      headerConfig={{
        className: 'desktop:hidden',
        navigateBack: true,
        actions: <AddMemberLink />,
      }}>
      {isDesktop && (
        <>
          <Linkable
            linkable={canManage}
            to={ROUTES.MESSAGES.GROUPS.SETTINGS.ADMIN.INFO.$buildPath({ params: { id: groupId } })}>
            <GroupSettingHeader
              groupId={groupId}
              groupName={GroupInfo?.GroupData?.GroupName || ''}
              canManage={canManage}
              className='flex desktop:py-0 desktop:px-4'
            />
          </Linkable>
          <div className='justify-between  flex px-4 text-body1 h-12 items-center'>
            <span>{t('conversation.setting.group.id')}</span>
            <span className='text-secondary'>{GroupInfo?.GroupData?.GroupCode}</span>
          </div>
        </>
      )}
      <div className='mx-3 desktop:mx-0'>
        <SearchInput
          icon
          keyword={keyword}
          className='desktop:mx-0 my-0 '
          setKeyword={setKeyword}
          clearKeyword={keyword ? () => setKeyword('') : undefined}
        />
      </div>

      <h2 className='mx-5 font-bold text-body1 flex justify-between items-center desktop:ml-4 desktop:mr-1'>
        <span>{`${t('conversation.setting.group.member')} (${filteredMembers.length})`}</span>
        <AddMemberLink className='hidden desktop:inline' />
      </h2>

      <Virtuoso
        data={sort((a, b) => (a.Role > b.Role ? -1 : 1), filteredMembers)}
        overscan={6}
        defaultItemHeight={56}
        increaseViewportBy={168}
        className='mx-5 flex flex-1 flex-col items-stretch gap-5'
        itemContent={(_, member) => (
          <Linkable
            key={member.ID}
            linkable={userId !== member.UserID}
            to={ROUTES.CONTACT.USERS.BY_ID.$buildPath({ params: { userId: member.UserID }, searchParams: { groupId } })}
            className='w-full'>
            <div className='flex items-center gap-2 py-2'>
              <div className='flex min-w-0 flex-1 items-center gap-4'>
                <AvatarWithStatus
                  imageUrl={member.UserHeadImg}
                  label={member.UserNickName}
                  className='size-12'
                  size={[48, 48]}
                  defaultAvatar='group'
                />

                <div className='flex flex-col items-stretch justify-center gap-0.5 overflow-hidden'>
                  <span className='truncate break-all text-h2 desktop:text-body1'>{member.DisplayName}</span>
                  {member.RemarkName && (
                    <span className='text-h2 desktop:text-sm text-neutral-400'>
                      {member.GroupMemberName || member.UserNickName}
                    </span>
                  )}
                </div>
              </div>

              {canManage && member.Role < 9 ? (
                <>
                  <button
                    type='button'
                    className='btn btn-warning btn-sm text-inverse'
                    onClick={handleBanMember(member.UserID, member.Status === 0)}>
                    {member.Status === 0
                      ? t('conversation.setting.group.has-banned')
                      : t('conversation.setting.group.ban')}
                  </button>

                  <button
                    type='button'
                    className='btn btn-error btn-sm'
                    onClick={handleKickOutMember(member.UserID)}>
                    {t('conversation.setting.group.remove')}
                  </button>
                </>
              ) : member.Role === 9 || member.Role === 10 ? (
                <span
                  className={cn('badge badge-outline badge-lg min-w-16 text-xs', {
                    'text-secondary': member.Role === 10,
                    'text-primary': member.Role === 9,
                  })}>
                  {member.Role === 9
                    ? t('conversation.setting.group.administrator')
                    : member.Role === 10
                      ? t('conversation.setting.group.owner')
                      : null}
                </span>
              ) : (
                <></>
              )}
            </div>
          </Linkable>
        )}
      />
    </PageLayout>
  );
}
